import { PAEventType } from '@dadohr/shared/domainTypes/productAnalytics';
import styled from '@emotion/styled';
import {
  Button,
  Dialog,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography
} from '@material-ui/core';
import { Alert, Skeleton } from '@material-ui/lab';
import { useEffect, useMemo, useState } from 'react';

import { Functionality } from '../..';
import { ButtonWithPromise } from '../../../../../../components/ButtonWithPromise';
import { DialogActionsWithSlots } from '../../../../../../components/DialogActionsWithSlots';
import { DialogTitleWithTypography } from '../../../../../../components/DialogTitleWithTypography';
import { useSnackbar } from '../../../../../../hooks/useSnackbar';
import {
  useTranslation,
  useUnsafeTranslation
} from '../../../../../../i18next';
import { captureException } from '../../../../../../sentry';
import { useCurrentUser } from '../../../../../../services/currentUser';
import { useProductAnalytics } from '../../../../../../services/productAnalytics';
import {
  IntegrationConfigurationButton,
  IntegrationSetupDialogProperties
} from '../common';
import { OAuthSetupDialogV2 } from '../oauth';
import {
  disconnectRippling,
  RipplingStatusFilter,
  updateRipplingStatusFilter,
  useRipplingConnectionStatus
} from './service';

const NAME = 'Rippling';

const Content = styled(DialogContent)((p) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: p.theme.spacing(2)
}));

const filterLabel = (v: RipplingStatusFilter) =>
  v === 'ACTIVE' ? 'Active only' : 'Active + Terminated';

const SetupDialog = ({
  open,
  onClose,
  orgId
}: {
  open: boolean;
  onClose: () => void;
  orgId: string;
} & IntegrationSetupDialogProperties) => {
  const { t } = useTranslation();
  const { t: ut } = useUnsafeTranslation();
  const { trackEvent } = useProductAnalytics();
  const { enqueueSnackbarError } = useSnackbar();
  const [refreshKey, setRefreshKey] = useState(0);
  const [isUpdatingStatusFilter, setIsUpdatingStatusFilter] = useState(false);

  const [status, loading] = useRipplingConnectionStatus(orgId, refreshKey);
  const connected = status?.connected ?? false;
  const statusFilter = (status?.statusFilter ??
    'ACTIVE') as RipplingStatusFilter;

  // While dialog open and not connected, poll so it flips after OAuth completes in another tab
  useEffect(() => {
    if (!open || connected) return;
    const id = window.setInterval(() => setRefreshKey((k) => k + 1), 4000);
    return () => window.clearInterval(id);
  }, [open, connected]);

  if (loading && !status) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitleWithTypography>
          {NAME} {t('org.integrations.integration')}
        </DialogTitleWithTypography>
        <Content>
          <Skeleton />
          <Skeleton />
        </Content>
        <DialogActionsWithSlots
          right={
            <Button color="primary" onClick={onClose}>
              {t('Close')}
            </Button>
          }
        />
      </Dialog>
    );
  }

  if (!connected) {
    return (
      <OAuthSetupDialogV2
        orgId={orgId}
        open={open}
        onClose={onClose}
        name={NAME}
        oauthCf="rippling_oauthGetAuthorizeUrl"
        customButton={(onClick) => (
          <ButtonWithPromise
            variant="contained"
            color="primary"
            onClick={async () => {
              await trackEvent({
                eventType: PAEventType.OTHER,
                name: 'Integration Connection Started',
                Explanation: 'User clicks Connect to Rippling',
                'Organisation ID': orgId,
                'Integration Type': 'RIPPLING'
              });
              await onClick();
            }}
            pending={`${t('org.integrations.actions.redirecting')}...`}
          >
            {t('org.integrations.actions.setup')}
          </ButtonWithPromise>
        )}
      >
        <Alert severity="info" icon={false}>
          {ut('Connect Rippling to enable importing employee data.')}
        </Alert>
        <Typography variant="body2">
          {ut(
            'You will be redirected to Rippling to approve access, then returned here.'
          )}
        </Typography>
      </OAuthSetupDialogV2>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitleWithTypography>
        {NAME} {t('org.integrations.integration')}
      </DialogTitleWithTypography>

      <Content>
        <Alert severity="success">{ut('Connected')}</Alert>

        <FormControl variant="outlined" fullWidth>
          <InputLabel id="rippling-status-filter-label">
            {ut('Employee status filter')}
          </InputLabel>
          <Select
            labelId="rippling-status-filter-label"
            value={statusFilter}
            disabled={isUpdatingStatusFilter}
            onChange={async (e) => {
              const v = e.target.value as RipplingStatusFilter;
              setIsUpdatingStatusFilter(true);
              try {
                await updateRipplingStatusFilter(orgId, v);
                setRefreshKey((k) => k + 1);
              } catch (err) {
                enqueueSnackbarError(
                  ut(
                    'Failed to update Rippling status filter. Please try again.'
                  )
                );
                captureException(err, {
                  tags: {
                    feature: 'rippling',
                    action: 'update_status_filter'
                  },
                  extra: {
                    orgId,
                    statusFilter: v
                  },
                  level: 'error'
                });
              } finally {
                setIsUpdatingStatusFilter(false);
              }
            }}
            label={ut('Employee status filter')}
          >
            <MenuItem value="ACTIVE">{filterLabel('ACTIVE')}</MenuItem>
            <MenuItem value="ACTIVE_AND_TERMINATED">
              {filterLabel('ACTIVE_AND_TERMINATED')}
            </MenuItem>
          </Select>
        </FormControl>

        <Typography variant="caption" color="textSecondary">
          {ut(
            'This controls which employees are included when importing from Rippling.'
          )}
        </Typography>
      </Content>

      <DialogActionsWithSlots
        left={
          <ButtonWithPromise
            variant="contained"
            color="secondary"
            onClick={async () => {
              await disconnectRippling(orgId);
              await trackEvent({
                eventType: PAEventType.OTHER,
                name: 'Integration Disconnected',
                Explanation: 'User disconnects Rippling integration',
                'Organisation ID': orgId,
                'Integration Type': 'RIPPLING'
              });
              setRefreshKey((k) => k + 1);
            }}
            pending={`${ut('Disconnecting')}...`}
          >
            {ut('Disconnect')}
          </ButtonWithPromise>
        }
        right={
          <Button color="primary" onClick={onClose}>
            {t('Close')}
          </Button>
        }
      />
    </Dialog>
  );
};

export const RipplingSettingsButton = ({
  functionalities,
  interactive
}: {
  functionalities: Functionality[];
  interactive: boolean;
}) => {
  const { org } = useCurrentUser();
  const [status] = useRipplingConnectionStatus(org.id, 0);
  const active = useMemo(() => status?.connected ?? false, [status]);

  return (
    <IntegrationConfigurationButton
      functionalities={functionalities}
      name={NAME}
      logoUrl="/images/integrations-logos/rippling.png"
      active={active}
      interactive={interactive}
      renderSetupDialog={({
        open,
        onClose,
        onFailure,
        onSuccessfulIntegration
      }: IntegrationSetupDialogProperties) => (
        <SetupDialog
          orgId={org.id}
          open={open}
          onClose={onClose}
          onFailure={onFailure}
          onSuccessfulIntegration={onSuccessfulIntegration}
        />
      )}
    />
  );
};
